import { FC } from 'react';
import dayjs from 'dayjs';
import { PersonsV3 } from '@frontend/api-person';
import { SMSDataV3 } from '@frontend/api-sms-data';
import { useMessageAction } from '@frontend/contact-actions-send-message';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useAppScopeStore } from '@frontend/scope';
import { Text, Avatar, SpinningLoader, Button } from '@frontend/design-system';
import { useTaskFormPatient } from '../../../hooks';
import { Citation } from '../../../types';
import { citationContainerStyle, messageStyle, alignRight, noCitationContainerStyle } from './styles';

interface MessageCitationProps {
  sourceId?: string;
  citation?: Citation;
}

export const MessageCitation: FC<MessageCitationProps> = ({ sourceId, citation }) => {
  const { t } = useTranslation('task-center');
  const { selectedLocationIds } = useAppScopeStore();

  // TODO: fetch only when we want to click on the message via deep-linking
  const { data, isLoading, isFetching } = SMSDataV3.Queries.useGetSMSQuery({
    request: {
      locationId: selectedLocationIds[0],
      smsId: sourceId,
    },
  });

  const { onClick: openThread } = useMessageAction();
  const { patient, isLoading: isFetchingPatient } = useTaskFormPatient(citation?.patient_id);
  const date = citation?.created_at ? dayjs(citation?.created_at).format('MMM DD, YYYY, hh mm A') : '';
  const personName = patient ? PersonsV3.PersonHelpers.getFullName(patient) : t('Patient Name');

  const onClickView = () => {
    openThread({
      threadGroupId: selectedLocationIds[0],
      threadId: data?.sms.threadId,
      personPhone: data?.sms.personPhone,
      departmentId: data?.sms.departmentId,
      locationPhone: data?.sms.locationPhone,
      personId: data?.sms.personId,
    });
  };

  if (!citation) {
    return (
      <div css={noCitationContainerStyle}>
        <Icon name='message' size={16} />{' '}
        <Text size='large' weight='bold'>
          {t('View Details in Inbox')}
        </Text>
        <Button variant='tertiary' loading={isLoading || isFetching} css={alignRight} onClick={onClickView}>
          {t('View')}
        </Button>
      </div>
    );
  }

  return (
    <div css={citationContainerStyle} onClick={onClickView}>
      <span>
        <Icon name='inbox' size={16} />
        <Text size='large' weight='bold'>
          {t('Inbox')}
        </Text>
      </span>
      <span>
        {isFetchingPatient ? <SpinningLoader /> : personName && <Avatar name={personName} />}
        {citation?.description && (
          <Text size='large' css={messageStyle}>
            {citation?.description}
          </Text>
        )}
      </span>
      <span>
        {personName && (
          <Text size='medium' weight='bold'>
            {personName}
          </Text>
        )}
        <Text color='light' size='small' css={alignRight}>
          {date}
        </Text>
      </span>
    </div>
  );
};
